import { ApolloClient, HttpLink, InMemoryCache, from } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";

import { jwtExpiryMs } from "../../auth/jwt";
import { getPpDocScope } from "./ppDocScope";

// care-management-gql accepts the browser's bearer token directly (Entra AD
// and the two B2C issuers all validate on its side), so Apollo posts straight
// there — no proxy backend in the middle.
const GQL_URL = import.meta.env.VITE_CARE_MANAGEMENT_GQL_ENDPOINT ?? "";

export type TokenGetter = () => string | undefined | null;
export type AuthFailureHandler = () => void;

interface ClientOptions {
  getToken: TokenGetter;
  /**
   * Optional getter for the Provider Portal B2C access token. When present and
   * non-null, it is forwarded as the `x-pp-bearer` request header so that
   * care-management-gql's CBP resolvers can authenticate against the PP REST
   * API on the caller's behalf. Empty when the user has not logged in via the
   * provider-portal-b2c provider.
   */
  getPpToken?: TokenGetter;
  /**
   * Called when the server rejects a request in a way that means the session
   * is dead — 401/403, a GraphQL `UNAUTHENTICATED` error, or a 500 that
   * arrives after authLink already dropped the bearer because the local token
   * was expired. Caller should clear stored tokens and redirect to /login.
   */
  onAuthFailure: AuthFailureHandler;
  /**
   * Override the GraphQL endpoint URL. Defaults to VITE_CARE_MANAGEMENT_GQL_ENDPOINT
   * so all existing call sites work without change.
   */
  endpointUrl?: string;
}

/**
 * Build an Apollo client whose authLink reads the current bearer token from
 * the supplied getter on every request. The getter abstracts over MSAL-silent
 * (standalone web) vs. bridged-from-extension (iframe) token sources, so the
 * client doesn't need to know which it is.
 *
 * AuthContext proactively refreshes tokens ~60s before `exp`, so the cached
 * token here is normally fresh. This link drops an already-expired token as
 * a belt-and-suspenders guard (system-sleep edge case): sending an
 * unauthenticated request surfaces as a clean auth error instead of the
 * noisy `JWTExpired` stack care-management-gql logs on verify failure.
 *
 * errorLink catches post-expiry failures so the app can't sit in a broken
 * half-auth state: if authLink dropped the bearer, care-management-gql
 * responds 500 (it treats missing-auth as a server error, not 401), and
 * without this link the UI would re-fire the same dead query forever.
 */
export function createMember360Client(
  opts: ClientOptions,
): ApolloClient<unknown> {
  const { getToken, getPpToken, onAuthFailure, endpointUrl = GQL_URL } = opts;
  const httpLink = new HttpLink({ uri: endpointUrl });

  const authLink = setContext((_, { headers }) => {
    const token = getToken();
    const extraHeaders: Record<string, string> = {};

    if (token) {
      const expMs = jwtExpiryMs(token);
      if (expMs === null || expMs > Date.now()) {
        extraHeaders["authorization"] = `Bearer ${token}`;
      }
    }

    // Forward the PP B2C token so care-management-gql's CBP resolvers can
    // call the Provider Portal REST API on behalf of the logged-in user.
    // Empty string when the token is unavailable — the header is omitted
    // rather than sent blank so the backend can distinguish "no token" from
    // "explicitly empty".
    if (getPpToken) {
      const ppToken = getPpToken();
      if (ppToken) {
        extraHeaders["x-pp-bearer"] = ppToken;
      }
    }

    // Forward the active member's IPA + MPI so the documents lib's by-uid
    // queries (getDocument/getDocumentAdditionalInfo/downloadDocument, which
    // send only a documentId) can be resolved server-side against PP's
    // GetMemberDocuments list. Harmless on other operations.
    const { companyId, membMpiNo } = getPpDocScope();
    if (companyId) extraHeaders["x-pp-company-id"] = companyId;
    if (membMpiNo) extraHeaders["x-pp-member-mpi"] = membMpiNo;

    return {
      headers: {
        ...headers,
        ...extraHeaders,
      },
    };
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    // Network-level auth failures. Rare against care-management-gql (which
    // normally returns HTTP 200 + GraphQL errors), but possible if a
    // reverse-proxy or a different backend ever sits in front.
    const statusCode =
      networkError && "statusCode" in networkError
        ? (networkError as { statusCode?: number }).statusCode
        : undefined;
    const isAuthStatus = statusCode === 401 || statusCode === 403;

    // care-management-gql's auth middleware throws `GraphQLError` with
    // `extensions.code` set to the string "401"/"403" and `extensions.name`
    // set to one of {AuthenticationError, JWTError}. Apollo Server's default
    // for missing auth is "UNAUTHENTICATED", included for forward-compat if
    // the backend ever normalises.
    const isAuthGql = graphQLErrors?.some((e) => {
      const code = e.extensions?.code;
      const name = e.extensions?.name;
      return (
        code === "UNAUTHENTICATED" ||
        code === "401" ||
        code === "403" ||
        name === "AuthenticationError" ||
        name === "JWTError"
      );
    });

    // Some JWT-verification failures (e.g. `exp` mismatch) slip past the
    // usual GraphQLError path and surface as a bare HTTP 500 with the JWT
    // error message in the response body. Scoped by keyword so an unrelated
    // server crash doesn't log every authenticated user out.
    const isJwtServerError =
      statusCode === 500 &&
      !!networkError &&
      /\bjwt\b|\bexp\b|expired|bearer|unauthoriz|authentic/i.test(
        networkError.message +
          " " +
          stringifyResult((networkError as { result?: unknown }).result),
      );

    if (isAuthStatus || isAuthGql || isJwtServerError) {
      onAuthFailure();
    }
  });

  return new ApolloClient({
    link: from([errorLink, authLink, httpLink]),
    cache: new InMemoryCache(),
  });
}

const stringifyResult = (result: unknown): string => {
  if (result == null) return "";
  try {
    return JSON.stringify(result);
  } catch {
    return "";
  }
};
